import { AppMainSection, commonButtonMessages, HomeState, useAppSelector, UserState } from 'azshared';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { MenuItem } from '../../components/menu-item/menu-item';
import { MenuItemInterface } from '../../utils/menu-items';
import './menu.scss';

export interface MenuProps {
  menuItems?: MenuItemInterface[];
  selectedSection?: AppMainSection | string;
  isLoggingOut?: boolean;
  opened?: boolean;
  closeGestureOffset?: number;
  onSelectedSectionChanged?: (section?: any) => void;
  onLogout?: () => void;
  onToggle?: () => void;
  children: ReactNode;
}

export const Menu = (props: MenuProps) => {
  const { formatMessage } = useIntl();
  const { selectedSection, onSelectedSectionChanged, menuItems, children, isLoggingOut, onLogout, onToggle, opened = true, closeGestureOffset = 0 } = props;
  const { isProspect } = useAppSelector<UserState>((state) => state.data.user);
  const { practiceData } = useAppSelector((state) => state.features.home as HomeState);

  const [openGestureOffset, setOpenGestureOffset] = useState<number>(0);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const shouldOpenMenuOnEnd = useRef<boolean>(false);
  const menuRef = useRef<HTMLBaseElement>(null);
  const initialPositionRef = useRef<number>(0);
  const SWIPE_THRESHOLD = 15;

  const isPortraitOrientation = () => {
    return window.innerHeight > window.innerWidth;
  };

  const handleTouchStart = (e: TouchEvent) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('app-menu-toggler')) return;

    if (menuRef.current && !opened) {
      const isPortrait = window.innerHeight > window.innerWidth;
      const computedStyle = getComputedStyle(menuRef.current);
      const positionValue = isPortrait ? parseFloat(computedStyle.bottom) : parseFloat(computedStyle.left);
      initialPositionRef.current = isNaN(positionValue) ? 0 : positionValue;
    }

    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    shouldOpenMenuOnEnd.current = false;
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!touchStartPos.current || opened) return;

    const touchCurrentPos = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };

    const deltaX = touchStartPos.current.x - touchCurrentPos.x;
    const deltaY = touchStartPos.current.y - touchCurrentPos.y;

    const isPortrait = isPortraitOrientation();

    // Portrait: open menu on swipe up (deltaY > SWIPE_THRESHOLD)
    if (isPortrait && deltaY > SWIPE_THRESHOLD) {
      e.preventDefault();
      shouldOpenMenuOnEnd.current = true;
      // Apply progressive offset - move menu up as user swipes up
      setOpenGestureOffset(deltaY);
    }
    // Landscape: open menu on swipe right (deltaX < -SWIPE_THRESHOLD)
    else if (!isPortrait && deltaX < -SWIPE_THRESHOLD) {
      e.preventDefault();
      shouldOpenMenuOnEnd.current = true;
      // Apply progressive offset - move menu right as user swipes right
      setOpenGestureOffset(-deltaX);
    }

    // Continue preventing default if the gesture is active
    if (shouldOpenMenuOnEnd.current) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (shouldOpenMenuOnEnd.current) {
      e.preventDefault();
      setOpenGestureOffset(0);
      if (onToggle) {
        onToggle();
      }
    }
    touchStartPos.current = null;
    shouldOpenMenuOnEnd.current = false;
  };

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false });
    document.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
    document.addEventListener('touchend', handleTouchEnd as EventListener, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart as EventListener);
      document.removeEventListener('touchmove', handleTouchMove as EventListener);
      document.removeEventListener('touchend', handleTouchEnd as EventListener);
    };
  }, [opened, onToggle]);

  const handleSelectedSection = useCallback(
    (section?: any) => {
      if (onSelectedSectionChanged) {
        onSelectedSectionChanged(section);
      }
    },
    [onSelectedSectionChanged]
  );

  const itemsToShow = useMemo(() => {
    if (isProspect) {
      const itemsToExclude = ['wallet', 'documents'];
      if (practiceData?.value?.statoPratica === 'Bozza') {
        itemsToExclude.push('sign');
      }
      return menuItems?.filter((item) => !itemsToExclude.includes(item.icon));
    }
    return menuItems;
  }, [isProspect, practiceData, menuItems]);

  const toggleMenu = () => {
    if (onToggle) {
      onToggle();
    }
  };

  const getMenuPositioning = () => {
    // Only apply positioning during gestures, let CSS handle initial state
    if (closeGestureOffset === 0 && openGestureOffset === 0) {
      return {};
    }

    const isPortrait = window.innerHeight > window.innerWidth;

    if (isPortrait) {
      // Portrait: use bottom positioning
      let bottom = 0;

      if (opened) {
        // Menu is open (bottom = 0), closing gesture moves it down
        bottom = -closeGestureOffset;
      } else {
        // Menu is closed, opening gesture moves it up from initial position
        bottom = initialPositionRef.current + openGestureOffset;
      }

      // Clamp between initial position and 0
      bottom = Math.max(initialPositionRef.current, Math.min(0, bottom));

      return { bottom: `${bottom}px` };
    } else {
      // Landscape: use left positioning
      let left = 0;

      if (opened) {
        // Menu is open (left = 0), closing gesture moves it left
        left = -closeGestureOffset;
      } else {
        // Menu is closed, opening gesture moves it right from initial position
        left = initialPositionRef.current + openGestureOffset;
      }

      // Clamp between initial position and 0
      left = Math.max(initialPositionRef.current, Math.min(0, left));

      return { left: `${left}px` };
    }
  };

  return (
    <aside
      ref={menuRef}
      className={`app-menu ${opened ? 'open' : ''}`}
      style={{
        ...getMenuPositioning(),
        transition: closeGestureOffset === 0 && openGestureOffset === 0 ? 'all 0.3s ease-out' : 'none'
      }}>
      <div className={`app-menu-toggler ${opened ? 'open' : ''}`} onClick={toggleMenu}>
        <div className='hamburger-icon'></div>
      </div>
      <div className='app-menu-navbar'>
        {children}
        <div className='app-menu-bar'>
          {itemsToShow?.map((item) => (
            <MenuItem
              key={item.section}
              icon={item.icon}
              label={formatMessage(item.label)}
              isSelected={selectedSection === item.section}
              section={item.section}
              onSelected={handleSelectedSection}
            />
          ))}
        </div>
      </div>
      <div className='app-menu-footer'>
        {onLogout && <MenuItem icon='logout' label={formatMessage(commonButtonMessages.logout)} onSelected={onLogout} isLoading={isLoggingOut} />}
        <div className='logo-azimut' />
      </div>
    </aside>
  );
};
